//CRN_20260522 - https://riskline.readme.io/reference/apis-overview

import { AccessGuids } from 'src/app/core/consts';
import { clearTripReadyResult, executeTripReady } from 'src/app/store/RiskIntelligence/risk_intelligence_action';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { RisklineCollectionsService } from 'src/app/core/services/riskline/riskline-collections.service';
import { RisklineCountryDTO } from 'src/app/core/models/system/riskline/riskline-collections.dto';
import { RisklineSharedService } from 'src/app/core/services/riskline/riskline-shared.service';
import { RisklineTripReadyService } from 'src/app/core/services/riskline/riskline-trip-ready.service';
import { RootReducerState } from 'src/app/store';
import { selectLastTripReadyResult, selectRiskIntelligenceLoading } from 'src/app/store/RiskIntelligence/risk_intelligence_selector';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { TripReadyRequestDTO_EmptyObject } from 'src/app/core/models/system/riskline/riskline-trip-ready.dto';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

export type TripReadyDisplayMode='landing' | 'json';

@Component({
  selector   : 'app-trip-ready-panel',
  templateUrl: './trip-ready-panel.component.html',
  styleUrls  : ['./trip-ready-panel.component.scss']
})
export class TripReadyPanelComponent implements OnDestroy, OnInit {

  @Input() public autoGenerate    : boolean              | undefined;
  @Input() public initialDestIso  : string               | undefined;
  @Input() public initialFromDate : string               | undefined;
  @Input() public initialMode     : TripReadyDisplayMode | undefined;
  @Input() public initialOriginIso: string               | undefined;
  @Input() public initialToDate   : string               | undefined;
  @Input() public lockMode        : TripReadyDisplayMode | undefined;

  @Output() public cleared      : EventEmitter<void>   =new EventEmitter<void>();
  @Output() public generated    : EventEmitter<void>   =new EventEmitter<void>();
  @Output() public landingActive: EventEmitter<boolean>=new EventEmitter<boolean>();


  get form() { return this.tripForm.controls; }
  
  get showModeToggle() { return this.componentAccessUserService.hasAccessGuid(AccessGuids.FormTripReadyShowModeToggle, true); }

  private countriesSub: Subscription | undefined;
  private loadingSub  : Subscription | undefined;
  private resultSub   : Subscription | undefined;

  public countries          : RisklineCountryDTO[]=[];
  public countriesLoadFailed: boolean=false;
  public displayMode        : TripReadyDisplayMode='landing';
  public formSubmitted      : boolean=false;
  public isLoading          : boolean=false;
  public landingUrl         : SafeResourceUrl | null=null;
  public result             : any=null;
  public tripForm!          : UntypedFormGroup;
  public validationError    : string='';

  constructor(
    private componentAccessUserService: ComponentAccessUserService,
    private domSanitizer              : DomSanitizer,
    private exceptionsService         : ExceptionsService,
    private formBuilder               : UntypedFormBuilder,
    private risklineCollectionsService: RisklineCollectionsService,
    private risklineSharedService     : RisklineSharedService,
    private risklineTripReadyService  : RisklineTripReadyService,
    private store                     : Store<{ data: RootReducerState; }>
  ) { }

  public ngOnInit(): void {
    try {
      this.displayMode=this.lockMode ?? this.initialMode ?? 'landing';

      this.buildForm();

      this.countriesSub=this.risklineCollectionsService.getCountries().subscribe({
        next : data  => {
          this.countries          =data ?? [];
          this.countriesLoadFailed=this.countries.length==0;
        },
        error: error => {
          this.countriesLoadFailed=true;
          this.exceptionsService.handleException(new Error(error));
        }
      });

      this.resultSub=this.store.select(selectLastTripReadyResult).subscribe({
        next: data => {
          this.result=data;
        }
      });

      this.loadingSub=this.store.select(selectRiskIntelligenceLoading).subscribe({
        next: loading => {
          this.isLoading=loading;
        }
      });

      if (this.autoGenerate)
        this.submitForm();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.countriesSub?.unsubscribe();
      this.loadingSub  ?.unsubscribe();
      this.resultSub   ?.unsubscribe();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public clearResult(): void {
    try {
      this.store.dispatch(clearTripReadyResult());
      this.formSubmitted  =false;
      this.landingUrl     =null;
      this.validationError='';
      this.landingActive.emit(false);
      this.cleared.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public selectMode(mode: TripReadyDisplayMode): void {
    try {
      if (this.lockMode)
        return;

      this.displayMode=mode;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public submitForm(): void {
    try {
      this.formSubmitted  =true;
      this.validationError='';

      if (!this.tripForm.valid)
        return;

      const dateValidation=this.risklineSharedService.validateDateRange(this.tripForm.value.fromDate, this.tripForm.value.toDate);

      if (!dateValidation.isValid) {
        this.validationError=dateValidation.message;
        return;
      }

      const destinationIso=(this.tripForm.value.destination || '').toLowerCase();
      const originIso     =(this.tripForm.value.origin      || '').toLowerCase();

      if (this.displayMode=='json') {
        const request=TripReadyRequestDTO_EmptyObject();
        request.departureDate=this.tripForm.value.fromDate;
        request.destination  =destinationIso;
        request.language     ='en';
        request.origin       =originIso;
        request.returnDate   =this.tripForm.value.toDate;

        this.store.dispatch(executeTripReady({ request }));
      }
      else {
        this.risklineTripReadyService.getLandingPageUrl(destinationIso).subscribe({
          next : url => {
            this.landingUrl=this.domSanitizer.bypassSecurityTrustResourceUrl(url);
            this.landingActive.emit(true);
          },
          error: err => {
            this.exceptionsService.handleException(new Error(err));
          }
        });
      }

      this.generated.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildForm(): void {
    try {
      const today=this.todayIso();

      this.tripForm=this.formBuilder.group({
        destination: [this.initialDestIso   ?? ''   , [Validators.required]],
        fromDate   : [this.initialFromDate  || today, [Validators.required]],
        origin     : [this.initialOriginIso ?? ''   , [Validators.required]],
        toDate     : [this.initialToDate    || today, [Validators.required]]
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private todayIso(): string {
    try {
      const now  =new Date();
      const year =now.getFullYear();
      const month=String(now.getMonth() + 1).padStart(2, '0');
      const day  =String(now.getDate()     ).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }
}
